import { useEffect, useState, type ReactNode } from 'react';
import { IconButton } from './Button.js';
import { TitleInfo } from './Info.js';

/**
 * Screen header: optional back arrow (sub-pages), eyebrow, title (h1 or h2), a line below (e.g. the connection chip),
 * right slot.
 */
export function TopBar({
  title,
  eyebrow,
  sub,
  right,
  back,
  info,
  as: Heading = 'h2',
}: {
  title: ReactNode;
  eyebrow?: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  back?: { label: string; onClick: () => void };
  /** an explanation of the page behind an (i) after the title */
  info?: { text: ReactNode; label: string; testId?: string } | null;
  as?: 'h1' | 'h2';
}) {
  // a sub-page's bar stays at the top (CSS); once the page scrolls under it, a line sets it off
  const hasBack = back !== undefined;
  const [lifted, setLifted] = useState(false);
  useEffect(() => {
    if (!hasBack) return;
    const onScroll = () => setLifted(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hasBack]);
  return (
    <header className={hasBack ? `topbar has-back${lifted ? ' is-lifted' : ''}` : 'topbar'}>
      {back && (
        <IconButton
          icon="chevronLeft"
          label={back.label}
          className="topbar-back"
          onClick={back.onClick}
          data-testid="page-back"
        />
      )}
      <div className="topbar-text">
        {eyebrow && <span className="label">{eyebrow}</span>}
        <TitleInfo info={info?.text} label={info?.label ?? ''} testId={info?.testId}>
          <Heading className="topbar-title">{title}</Heading>
        </TitleInfo>
        {sub && <div className="topbar-sub">{sub}</div>}
      </div>
      {right && <div className="topbar-right">{right}</div>}
    </header>
  );
}

/**
 * Round avatar that opens the account sheet. The address (and the operator role) is in the button as hidden
 * text, so it is known on every screen without opening the sheet.
 */
export function AccountButton({
  email,
  name,
  roleText,
  label,
  onClick,
}: {
  email: string;
  name?: string;
  roleText?: string;
  label: string;
  onClick: () => void;
}) {
  const initial = (name?.trim() || email).charAt(0).toUpperCase();
  return (
    <button type="button" className="avatar" onClick={onClick} aria-label={label} data-testid="account-open">
      <span aria-hidden="true">{initial}</span>
      <span className="visually-hidden" data-testid="account-email">
        {email}
        {roleText ? ` · ${roleText}` : ''}
      </span>
    </button>
  );
}
